// dependencies
import Immutable from "immutable";
import React, { useMemo, useState } from "react";

import Button from "../../../../../../../components/Button";
import ColorPicker from "../../../../../../../components/ColorPicker";
import { Grid, GridCol } from "../../../../../../../components/Grid";
import TextInput from "../../../../../../../components/Inputs/InputText";
import RadioButtons, { Option } from "../../../../../../../components/Inputs/SelectionOptions/RadioButtons";
import Spacer from "../../../../../../../components/Spacer";
// components
import Text from "../../../../../../../components/Text";

// utils
import { Hex } from "../../../../../../../utils/getContrast";

// constants
import { MAX_COHORT_NAME_LENGTH } from "../../../EngagementSetupConstants";
// schema
import { FormStateProps } from "../../index";
// styles
import css from "./SetupForm.module.scss";

const SetupForm = (props: FormStateProps) => {
	const { state, validate, updateState, isDuplication = false } = props;
	const [initialState, setInitialState] = useState(state && state.name === "");

	const validation = useMemo(() => validate(), [state]);

	const handleChange = (name: string, value: string) => {
		updateState({
			[name]: value,
		});
	};

	const handleNameChange = (value: string) => {
		handleChange("name", value);
		if (initialState) {
			setInitialState(false);
		}
	};

	const handleNextPage = () => {
		updateState({
			currentPage: state.cohortType === "static" ? "static-employees" : "dynamic-employees",
		});
	};

	return (
		<>
			<div className={css.formWrapper}>
				<div className={css.inputGroup}>
					<TextInput
						label="Cohort Name"
						value={state.name}
						className={css.input}
						placeholder="Insert cohort name"
						inputId="cohort-name"
						onChange={(value) => handleNameChange(value.trimStart())}
						maxLength={MAX_COHORT_NAME_LENGTH}
						errorMessage={!initialState ? (validation.errors.name?.message ?? undefined) : undefined}
					/>
				</div>

				<div className={css.inputGroup}>
					<ColorPicker
						label="Cohort Color"
						value={state.color as Hex}
						onChange={(value) => handleChange("color", value)}
					/>
				</div>

				<div className={css.inputGroup}>
					<TextInput
						label="Description"
						value={state.description}
						className={css.input}
						placeholder="Insert cohort description"
						inputId="cohort-description"
						onChange={(value) => handleChange("description", value)}
						maxLength={1000}
						errorMessage={validation.errors.description?.message ?? undefined}
					/>
				</div>
				{!isDuplication && (
					<div className={css.inputGroup}>
						<Text
							element="label"
							size="paragraph-2"
							weight="semi-bold"
							color="neutral-9"
							htmlFor="cohort-type"
						>
							Cohort Type
						</Text>

						<Spacer height={1} />
						<RadioButtons
							options={[
								{ value: "static", label: "Static List of Employees" },
								{ value: "dynamic", label: "Dynamic List of Employees" },
							]}
							value={{ value: state.cohortType } as Option}
							onChange={(value: Option | Immutable.Map<string, string>) => {
								if (value && "value" in value) {
									const cast = value as Option;
									handleChange("cohortType", cast.value);
								}
							}}
						/>
					</div>
				)}
			</div>

			<Spacer height={7} />

			<Grid gap={1} cols={2} className={css.buttonWrapper}>
				<GridCol>
					<Button href="/administration/engagement/cohorts" className={css.button} color="blue">
						Cancel
					</Button>
				</GridCol>

				<GridCol>
					<Button
						disabled={!validation.isValid}
						onClick={handleNextPage}
						className={css.button}
						color="green"
					>
						Next
					</Button>
				</GridCol>
			</Grid>
		</>
	);
};

export default SetupForm;
